import { useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { areEqual, createAvailabilitySummary } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { stringEqual } from '../../utils'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { LearnMoreDescription, learnMoreComponents } from './LearnMore'
import {
    bufferTimeOptions,
    confirmationStatusOptions,
    flexStartOptions,
    maxAvailableRangeOptions,
    requireCreditCardOptions,
    timeZoneOptions,
} from './Options'
import { createAvailabilityArray } from './SetupBusinessHoursModal'
import { SetupBusinessHoursModal, Weekday } from './SetupBusinessHoursModal'

const render = ({ base }: PocketComponentProps<void>) => {
    const [user, setUser] = useState(base.me.clone())
    const [bookingCapacity, setBookingCapacity] = useState(user.capacity ?? 1)
    const [availability, setAvailability] = useState<Weekday[]>(createAvailabilityArray(user))

    const [showAvailableHours, setShowAvailableHours] = useState(false)
    const [showUnavailableWarning, setShowUnavailableWarning] = useState(false)

    const [currentModal, setCurrentModal] = useState<keyof typeof learnMoreComponents | null>(null)
    const showLearnMore = (key: keyof typeof learnMoreComponents) => {
        setCurrentModal(key)
    }

    const navigate = usePSNavigate()

    const hasEdits =
        !stringEqual(user.bookingStatus, base.me.bookingStatus) ||
        !stringEqual(user.travel, base.me.travel) ||
        !stringEqual(user.confirmPayment, base.me.confirmPayment) ||
        !stringEqual(user.timezone, base.me.timezone) ||
        !stringEqual(user.availableRange, base.me.availableRange) ||
        user.bookingFlexStart !== base.me.bookingFlexStart ||
        user.bookingAssignment !== base.me.bookingAssignment ||
        bookingCapacity !== base.me.capacity ||
        !areEqual(createAvailabilityArray(user), availability)

    const onAvailableHoursDone = (weekdays: Weekday[]) => {
        setShowAvailableHours(false)
        setAvailability(weekdays)
    }

    const onSave = async (skipAvailabilityCheck?: boolean) => {
        if (!skipAvailabilityCheck) {
            const isUnavailable = !availability.some((day) => day.enabled)
            if (isUnavailable) {
                setShowUnavailableWarning(true)
                return
            }
        }
        user.capacity = bookingCapacity
        user.schedule = 0
        if (availability) {
            for (let i = 0; i < availability.length; i++) {
                const start = availability[i].start
                const end = availability[i].end
                const day = (['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const)[i]

                if (availability[i].enabled === false) {
                    user.setDayStartHour(day, undefined)
                    user.setDayEndHour(day, undefined)
                }
                else {
                    user.schedule += 1 << i
                    if (start) user.setDayStartHour(day, start.value)
                    if (end) user.setDayEndHour(day, end.value)
                }
            }
        }

        base.showProgressBar()
        const result = await RecordFactory.getInstance().sendUser(user)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        navigate('/settings/')
    }

    return (
        <WorkflowPage
            title="Scheduling"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={!hasEdits} onClick={() => onSave()}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                {((user.company && user.allowStaffing) || !(user.role === 'admin')) && (
                    <WorkflowPage.Card title="General">
                        {!(user.role === 'admin') && (
                            <>
                                <WorkflowPage.IconSection
                                    title="Require credit card"
                                    icon="bankCard.large"
                                    thirdLine={
                                        <LearnMoreDescription
                                            description="Require a credit card to enforce cancellation policies (Settings > Cancellations)"
                                            onClick={() => showLearnMore('require-cc')}
                                        />
                                    }
                                >
                                    <div className="flex flex-wrap gap-2">
                                        {requireCreditCardOptions.map((option) => (
                                            <Chip
                                                key={`cc-option-${option.value}`}
                                                title={option.label}
                                                isSelected={user.confirmPayment === option.value}
                                                onClick={() =>
                                                    setUser(
                                                        user.clone({
                                                            confirm_payment: option.value,
                                                        })
                                                    )
                                                }
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </WorkflowPage.IconSection>

                                <WorkflowPage.IconSection
                                    title="Confirmation Status"
                                    icon="form.large"
                                    thirdLine={
                                        <LearnMoreDescription
                                            description="Select whether an appointment is automatically confirmed"
                                            onClick={() => showLearnMore('confirm-status')}
                                        />
                                    }
                                >
                                    <div className="flex flex-wrap gap-2">
                                        {confirmationStatusOptions.map((option) => (
                                            <Chip
                                                key={`cc-option-${option.value}`}
                                                title={option.label}
                                                isSelected={user.bookingStatus === option.value}
                                                onClick={() =>
                                                    setUser(
                                                        user.clone({ booking_status: option.value })
                                                    )
                                                }
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </WorkflowPage.IconSection>
                            </>
                        )}

                        {user.company && user.allowStaffing && (
                            <WorkflowPage.IconSection
                                title="Staff appointments"
                                icon="employeeCard.large"
                                description="Choose how you want to handle staff appointments to unstaffed jobs"
                            >
                                <div className="flex flex-wrap gap-2">
                                    {User.assignmentStrings().map((option) => (
                                        <Chip
                                            key={`assignment-option-${option.id}`}
                                            title={option.label ?? ''}
                                            isSelected={user.bookingAssignment === option.id}
                                            onClick={() =>
                                                setUser(
                                                    user.clone({ booking_assignment: option.id })
                                                )
                                            }
                                            size="small"
                                        />
                                    ))}
                                </div>
                            </WorkflowPage.IconSection>
                        )}
                    </WorkflowPage.Card>
                )}

                <WorkflowPage.Card title="Availability">
                    <WorkflowPage.IconSection
                        title="Timezone"
                        icon="globeClock.large"
                        description="Availability is determined based on this timezone"
                    >
                        <div className="flex flex-wrap gap-2">
                            {timeZoneOptions.map((option) => (
                                <Chip
                                    key={`cc-option-${option.value}`}
                                    title={option.label}
                                    isSelected={user.timezone === option.value}
                                    onClick={() => setUser(user.clone({ timezone: option.value }))}
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="My hours"
                        icon="windowClock.large"
                        hasHoverEffect
                        secondaryView={
                            <button
                                className="link text-primary-700 text-sm underline"
                                onClick={() => setShowAvailableHours(true)}
                            >
                                Edit
                            </button>
                        }
                        thirdLine={
                            <div className="text-secondary-400 font-normal">
                                {createAvailabilitySummary(
                                    availability,
                                    'No availability set'
                                )}{' '}
                            </div>
                        }
                    />

                    {!(user.role === 'admin') && (
                        <>
                            <WorkflowPage.IconSection
                                title="Double booking"
                                icon="calendar.large"
                                description="Allow multiple clients to select the same time slot"
                                hasHoverEffect
                                secondaryView={
                                    <Checkbox
                                        isChecked={bookingCapacity > 1}
                                        onChange={(checked) => setBookingCapacity(checked ? 10 : 1)}
                                    />
                                }
                            />

                            <WorkflowPage.IconSection
                                title="Clients can't book beyond"
                                icon="alarmClock.large"
                                description="Control how far in advance clients can book you"
                            >
                                <SearchableSelect
                                    size="medium"
                                    className="border-gray-500"
                                    items={maxAvailableRangeOptions.map((option) => ({
                                        id: option.value,
                                        title: option.label,
                                    }))}
                                    onSelectItem={(item) =>
                                        setUser(user.clone({ available_range: item?.id }))
                                    }
                                    selectedItemID={user.availableRange}
                                    placeholder="None"
                                />
                            </WorkflowPage.IconSection>
                        </>
                    )}

                    <WorkflowPage.IconSection
                        title="Flex start time"
                        icon="clock.large"
                        thirdLine={
                            <LearnMoreDescription
                                description="Select when your appointments start"
                                onClick={() => showLearnMore('flex-start')}
                            />
                        }
                    >
                        <div className="flex flex-wrap gap-2">
                            {flexStartOptions.map((option) => (
                                <Chip
                                    key={`cc-option-${option.value}`}
                                    title={option.label}
                                    isSelected={user.bookingFlexStart === option.value}
                                    onClick={() =>
                                        setUser(user.clone({ booking_flex_start: option.value }))
                                    }
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Buffer time"
                        icon="windowClock.large"
                        thirdLine={
                            <LearnMoreDescription
                                description="Set time between appointments for a break, travel or extra prep"
                                onClick={() => showLearnMore('buffer-time')}
                            />
                        }
                    >
                        <div className="flex flex-wrap gap-2">
                            {bufferTimeOptions(user.type === 'payee').map((option) => (
                                <Chip
                                    key={`buffer-time-option-${option.value}`}
                                    title={option.label}
                                    isSelected={user.travel === option.value}
                                    onClick={() => setUser(user.clone({ travel: option.value }))}
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={showUnavailableWarning}
                title="Setup your business hours so clients can book you"
                onClose={() => setShowUnavailableWarning(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="primary"
                            onClick={() => {
                                onSave(true)
                                setShowUnavailableWarning(false)
                            }}
                        >
                            Continue
                        </Button>
                    </div>
                }
            >
                Without adding business hours your clients will not see your availability when they
                are booking you.
            </Modal>

            <Modal
                isOpen={currentModal !== null}
                onClose={() => setCurrentModal(null)}
                title={currentModal ? learnMoreComponents[currentModal].title : ''}
            >
                <div className="text-gray-600">
                    {currentModal && learnMoreComponents[currentModal].body}
                </div>
            </Modal>
            <SetupBusinessHoursModal
                isOpen={showAvailableHours}
                title="Setup service hours"
                onClose={() => setShowAvailableHours(false)}
                onDone={onAvailableHoursDone}
                currentAvailability={availability}
            />

            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}

export const SetupSchedulingPage = usePSComponent(render)
